/**
 * `<CopyFromScreen>` — the builder's **Copy from screen…**, in the Columns tab
 * and the Conditions tab (`docs/plans/group-sharing.md`, mock 3).
 *
 *     [Copy from screen… ▾]
 *      └ JAS · Impulse   JP · Persons
 *          ☑ PPS Trend  ☑ LCC Trend  ☐ HCD/LCD …
 *          [Add 2 columns]
 *
 * Lists what the host injected and asks the host's loader for one screen; the
 * copy itself is `lib/copy-from-screen.ts`. What already exists on this screen
 * is shown but cannot be ticked, and the note says how many were left out — a
 * button that added fewer than it promised would read as a broken one.
 */

import { useEffect, useRef, useState } from "react";
import type { SavedScreenerFilter, ScreenerColumn } from "@qufolio/model";

import {
  copySourcesByOwner,
  type CopyFromScreen as CopyFromScreenBinding,
  type CopySourceDefinition,
  type CopySourceScreen,
} from "../lib/copy-from-screen.js";
import { isSealed } from "../lib/saved-filters.js";
import { usePopoverPlace } from "./popover-place.js";

type Props =
  | {
      kind: "columns";
      source: CopyFromScreenBinding;
      /** The screen being edited — never offered as its own source. */
      current: string | undefined;
      have: readonly ScreenerColumn[];
      onAdd: (picked: ScreenerColumn[]) => void;
    }
  | {
      kind: "conditions";
      source: CopyFromScreenBinding;
      current: string | undefined;
      have: readonly SavedScreenerFilter[];
      onAdd: (picked: SavedScreenerFilter[]) => void;
    };

/** One copyable row, whichever kind: what it is called and whether it is here. */
interface Item {
  id: string;
  label: string;
  present: boolean;
  /** A sealed condition: listed so its absence is explained, never tickable. */
  sealed?: boolean;
}

export function CopyFromScreen(props: Props) {
  const { kind, source, current } = props;
  const [open, setOpen] = useState(false);
  // A press anywhere else closes it — so it and its neighbour (Copy / Link)
  // cannot both stand open on top of each other (owner, 2026-10-16).
  const rootRef = useRef<HTMLSpanElement | null>(null);
  const place = usePopoverPlace(open, rootRef, 320);
  useEffect(() => {
    if (!open) return;
    const away = (event: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", away);
    return () => document.removeEventListener("pointerdown", away);
  }, [open]);
  const [from, setFrom] = useState<CopySourceScreen | null>(null);
  const [loaded, setLoaded] = useState<CopySourceDefinition | null | "loading">(null);
  const [ticked, setTicked] = useState<ReadonlySet<string>>(new Set());

  const groups = copySourcesByOwner(source.screens, current);
  const noun = kind === "columns" ? "column" : "condition";

  const pick = (screen: CopySourceScreen) => {
    setFrom(screen);
    setTicked(new Set());
    setLoaded("loading");
    void source.load(screen.key).then(
      (definition) => setLoaded(definition),
      () => setLoaded(null),
    );
  };

  const items: Item[] =
    loaded && loaded !== "loading"
      ? kind === "columns"
        ? (loaded.columns ?? []).map((c) => ({
            id: c.field,
            label: c.header || c.field,
            present: props.have.some((h) => h.field === c.field),
          }))
        : (loaded.savedFilters ?? []).map((f) => ({
            id: f.id,
            label: f.name,
            present: (props.have as readonly SavedScreenerFilter[]).some(
              (h) => h.id === f.id || h.name.trim() === f.name.trim(),
            ),
            ...(isSealed(f) ? { sealed: true } : {}),
          }))
      : [];
  const present = items.filter((i) => i.present).length;
  const sealedCount = items.filter((i) => i.sealed && !i.present).length;
  /** Neither here already nor sealed — what a tick can add. */
  const copyable = (i: Item) => !i.present && !i.sealed;
  const count = items.filter((i) => copyable(i) && ticked.has(i.id)).length;

  const add = () => {
    if (!loaded || loaded === "loading") return;
    if (props.kind === "columns") {
      props.onAdd((loaded.columns ?? []).filter((c) => ticked.has(c.field)));
    } else {
      props.onAdd((loaded.savedFilters ?? []).filter((f) => ticked.has(f.id) && !isSealed(f)));
    }
    setOpen(false);
    setFrom(null);
    setLoaded(null);
    setTicked(new Set());
  };

  return (
    <span ref={rootRef} style={{ position: "relative", display: "inline-block" }}>
      <button
        type="button"
        onClick={() => setOpen((was) => !was)}
        aria-expanded={open}
        title={`Copy ${noun}s from another screen you can read`}
        style={BUTTON}
      >
        Copy from screen… ▾
      </button>
      {open && (
        <div role="dialog" aria-label="Copy from screen" style={{ ...POPOVER, ...place }}>
          {groups.length === 0 && <div style={NOTE}>No other screen to copy from.</div>}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {groups.flatMap(({ owner, screens }) =>
              screens.map((screen) => (
                <button
                  key={screen.key}
                  type="button"
                  onClick={() => pick(screen)}
                  style={{
                    ...BUTTON,
                    ...(from?.key === screen.key
                      ? { background: "var(--qf-selection)", color: "var(--qf-text)" }
                      : {}),
                  }}
                >
                  {owner} · {screen.name}
                </button>
              )),
            )}
          </div>
          {loaded === "loading" && <div style={NOTE}>Loading…</div>}
          {from && loaded === null && <div style={NOTE}>That screen could not be read.</div>}
          {from && loaded && loaded !== "loading" && (
            <>
              {items.length === 0 && <div style={NOTE}>It has no {noun}s.</div>}
              <div style={LIST}>
                {items.map((item) => (
                  <label
                    key={item.id}
                    style={{ ...ROW, ...(!copyable(item) ? { color: "var(--qf-text-muted)" } : {}) }}
                    title={
                      item.present
                        ? `Already on this screen — not copied`
                        : item.sealed
                          ? `${from.ownerName}'s rule — applied on the server, not copied`
                          : undefined
                    }
                  >
                    <input
                      type="checkbox"
                      disabled={!copyable(item)}
                      checked={copyable(item) && ticked.has(item.id)}
                      onChange={(e) =>
                        setTicked((had) => {
                          const next = new Set(had);
                          if (e.target.checked) next.add(item.id);
                          else next.delete(item.id);
                          return next;
                        })
                      }
                    />
                    {item.label}
                    {item.sealed && " 🔒"}
                  </label>
                ))}
              </div>
              {present > 0 && (
                <div style={NOTE}>
                  {present} already on this screen — skipped
                </div>
              )}
              {sealedCount > 0 && (
                <div style={NOTE}>
                  {sealedCount} sealed — {from.ownerName}'s rules stay on their screen
                </div>
              )}
              <button type="button" disabled={count === 0} onClick={add} style={PRIMARY}>
                Add {count} {noun}
                {count === 1 ? "" : "s"}
              </button>
            </>
          )}
        </div>
      )}
    </span>
  );
}

/** `smallButton` and `primaryButton` in `ScreenerBuilder`, so it sits among them unnoticed. */
const BUTTON: React.CSSProperties = {
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  padding: "0.05rem 0.35rem",
  background: "transparent",
  color: "var(--qf-text-secondary)",
  cursor: "pointer",
  fontSize: "0.74rem",
};

const PRIMARY: React.CSSProperties = {
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  padding: "0.2rem 0.55rem",
  marginTop: 6,
  background: "var(--qf-surface-alt)",
  color: "var(--qf-text)",
  cursor: "pointer",
  fontSize: "0.76rem",
};

const POPOVER: React.CSSProperties = {
  position: "absolute",
  top: "100%",
  right: 0,
  zIndex: 20,
  marginTop: 4,
  width: 320,
  maxWidth: "80vw",
  padding: 8,
  borderRadius: 6,
  border: "1px solid var(--qf-border)",
  background: "var(--qf-surface)",
  color: "var(--qf-text)",
};

const LIST: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: "2px 10px",
  marginTop: 6,
  maxHeight: 200,
  overflowY: "auto",
};

const ROW: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  fontSize: "0.76rem",
};

const NOTE: React.CSSProperties = {
  marginTop: 6,
  fontSize: "0.72rem",
  color: "var(--qf-text-muted)",
};
